/**
 * lib/imageResize.ts
 * ------------------------------------------------------------------
 * Client-side image normalisation for the site-image library.
 *
 * Every slot on the public page has one exact size it must be (the
 * browser frames are 16:10, the social card is 1.91:1, …). Rather than
 * asking anyone to crop in an image editor, we cover-crop and rescale
 * in a canvas at upload time, so what lands in Storage is already the
 * right shape and a sensible weight.
 *
 * Crop anchoring is deliberate: excess HEIGHT is trimmed from the
 * bottom, never centred. Uploads are nearly always full-page website
 * screenshots, and the frames render them with `object-position: top`
 * — so keeping the top of the page is what makes the result look
 * intentional. Excess WIDTH is centred, which is right for wide shots.
 * ------------------------------------------------------------------
 */

/** Longest edge we will ever encode, as a guard against absurd uploads. */
const MAX_EDGE = 2400;

export interface ResizeResult {
  blob: Blob;
  /** Data URL for immediate preview without a round-trip to Storage. */
  preview: string;
  width: number;
  height: number;
  /** 'image/webp' where supported, otherwise 'image/jpeg'. */
  contentType: string;
  /** Encoded byte length. */
  bytes: number;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

let webpSupport: boolean | null = null;

/** Canvas WebP encoding is near-universal, but verify rather than assume. */
function supportsWebp(): boolean {
  if (webpSupport !== null) return webpSupport;
  const c = document.createElement('canvas');
  c.width = 1;
  c.height = 1;
  webpSupport = c.toDataURL('image/webp').startsWith('data:image/webp');
  return webpSupport;
}

/**
 * Cover-crop `file` to exactly `targetW` × `targetH` and encode it.
 *
 * @param quality 0–1 encoder quality. 0.86 is the sweet spot for UI
 *                screenshots — text stays crisp, files stay small.
 */
export async function resizeToSlot(
  file: File,
  targetW: number,
  targetH: number,
  quality = 0.86,
): Promise<ResizeResult> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Choose an image file (PNG, JPG or WebP).');
  }

  const img = await loadImage(file);
  const sw = img.naturalWidth;
  const sh = img.naturalHeight;
  if (!sw || !sh) throw new Error('That image appears to be empty.');

  // Never upscale beyond the source, and never exceed the guard rail.
  const scale = Math.min(1, MAX_EDGE / Math.max(targetW, targetH));
  const outW = Math.round(Math.min(targetW, sw < targetW ? sw : targetW) * scale) || targetW;
  const outH = Math.round(outW * (targetH / targetW));

  // Cover-crop rectangle in source coordinates.
  const targetRatio = targetW / targetH;
  const sourceRatio = sw / sh;
  let cropW = sw;
  let cropH = sh;
  let cropX = 0;
  let cropY = 0;

  if (sourceRatio > targetRatio) {
    // Source is wider than the slot — trim the sides, keep the centre.
    cropW = Math.round(sh * targetRatio);
    cropX = Math.round((sw - cropW) / 2);
  } else if (sourceRatio < targetRatio) {
    // Source is taller than the slot — keep the TOP, trim the bottom.
    cropH = Math.round(sw / targetRatio);
    cropY = 0;
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not process the image.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  // White base so transparent PNGs don't encode as black in JPEG.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, outW, outH);

  const contentType = supportsWebp() ? 'image/webp' : 'image/jpeg';
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, contentType, quality),
  );
  if (!blob) throw new Error('The image could not be encoded.');

  return {
    blob,
    preview: canvas.toDataURL(contentType, quality),
    width: outW,
    height: outH,
    contentType,
    bytes: blob.size,
  };
}

/** Human-readable file size, e.g. "184 KB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
